import { useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Loader2, API, toast } from "@/pages/shared";
import { Textarea } from "@/components/ui/textarea";
import { Brain, Save } from "lucide-react";

const FIELDS = [
  { key: "description", label: "What is your business?", placeholder: "e.g. I'm a fractional CFO for SaaS startups under £2m ARR", textarea: true },
  { key: "products", label: "Products & services", placeholder: "e.g. Monthly CFO retainer £1,500; one-off financial model £600" },
  { key: "audience", label: "Target customers", placeholder: "e.g. SaaS founders, 5-30 staff, raising or post-seed" },
  { key: "problems", label: "Problems you solve", placeholder: "e.g. Founders don't know their runway or unit economics" },
  { key: "pricing", label: "Pricing & offers", placeholder: "e.g. Free audit call → retainer; Black Friday 20% off" },
  { key: "website", label: "Website / key pages", placeholder: "e.g. https://yoursite.com, /pricing, /book-a-call" },
  { key: "ctas", label: "Preferred CTAs", placeholder: "e.g. Book a free runway review" },
  { key: "preferred_channels", label: "Preferred channels", placeholder: "e.g. LinkedIn first, X second" },
];

export const BusinessBrainSection = () => {
  const [brain, setBrain] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/account/business-brain`)
      .then((r) => setBrain(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/account/business-brain`, brain);
      toast.success("Business Brain saved — future generations will use this context");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <Card className="glass" data-testid="settings-business-brain-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-violet-500" /> Business Brain</CardTitle>
        <p className="text-sm text-muted-foreground">Your persistent business context. Every campaign and generation uses it, so you stop re-explaining your niche each time. The more specific, the better the output.</p>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-violet-500" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
                  <Label className="text-sm">{f.label}</Label>
                  {f.textarea ? (
                    <Textarea value={brain[f.key] || ""} onChange={(e) => setBrain({ ...brain, [f.key]: e.target.value })} placeholder={f.placeholder} maxLength={1000} rows={2} className="mt-1 bg-white/5 border-white/10" data-testid={`business-brain-${f.key}`} />
                  ) : (
                    <Input value={brain[f.key] || ""} onChange={(e) => setBrain({ ...brain, [f.key]: e.target.value })} placeholder={f.placeholder} maxLength={1000} className="mt-1 bg-white/5 border-white/10" data-testid={`business-brain-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
            <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="business-brain-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Business Brain
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
